import React, { Fragment, useState } from "react";
import { Box, Text, TableContainer, Flex, Table, useToken } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import hexToRgba from "hex-to-rgba";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import OutlinedButton from "../../components/themeComponents/OutlinedButton";
import { OvalSpinner } from "../../components/Loaders";
import { getAllContentPersonalization } from "../../Services/api";
import ContentPersonalizationFilter from "./ContentPersonalizationFilter";
import TableHead from "./TableHead";
import ContentPersonalizationTable from "./ContentPersonalizationTable";
import Pagination from "../../components/Pagination"; // Import the generic Pagination component

function ContentPersonalizationList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mainColor] = useToken("colors", ["primary.main"]);
  const [totalCount, setTotalCount] = useState(0);
  const [filterState, setFilterState] = useState({ keyword: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const cpQuery = useQuery(
    ["contentPersonalizationList", currentPage, filterState],
    async () => {
      const res = await getAllContentPersonalization(currentPage, filterState);
      setTotalCount(res?.count ?? 0);
      return res.data || [];
    },
    {
      staleTime: 0, // Ensure fresh data on page change
      retry: 1,
      enabled: true, // Always enabled
    }
  );

  const toggleArchived = (archived, id) => {
    const updatedData = cpQuery.data?.map((item) =>
      item.id === id ? { ...item, archived } : item
    ) || [];
    queryClient.setQueryData(["contentPersonalizationList", currentPage, filterState], updatedData);
  };

  return (
    <Fragment>
      <Box h="100%" overflow="hidden" display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
          <Text fontSize="xl" as="b">
            Content Personalization
          </Text>
          <OutlinedButton
            mb="5px"
            label="Create New"
            onClick={() => navigate("/content-personalization/new")}
          />
        </Box>
        <ContentPersonalizationFilter
          onFilter={(newState) => {
            setFilterState(newState);
            setCurrentPage(1); // Reset to first page on filter change
          }}
        />
        <TableContainer bg="white" position="relative" p={3} overflowY="auto">
          {cpQuery.isLoading && (
            <Flex justifyContent="center" alignItems="center" h="200px">
              <OvalSpinner width={50} height={50} />
            </Flex>
          )}
          {cpQuery.isError && (
            <Flex justifyContent="center" alignItems="center" py={10}>
              <Text color="red.500">Error loading records. Please try again.</Text>
            </Flex>
          )}
          {!cpQuery.isLoading && !cpQuery.isError && (
            <Table>
              {cpQuery.data?.length > 0 ? (
                <Fragment>
                  <TableHead headersProps={["Name", "Description", "Created", "Actions"]} />
                  <ContentPersonalizationTable
                    list={cpQuery.data}
                    refetch={() => cpQuery.refetch()}
                  />
                </Fragment>
              ) : (
                <Flex direction="column" justifyContent="center" alignItems="center" py={10}>
                  <Text fontSize="lg" fontWeight="bold" color="gray.600">
                    No records found.
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Create a new record to get started.
                  </Text>
                  <PrimaryButton
                    label="Create Now"
                    mt={4}
                    onClick={() => navigate("/content-personalization/new")}
                    size="md"
                  />
                </Flex>
              )}
            </Table>
          )}
          {!cpQuery.isLoading && cpQuery?.data?.length > 0 && (
            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              isDisabled={cpQuery.isLoading}
            />
          )}
        </TableContainer>
      </Box>
    </Fragment>
  );
}

export default ContentPersonalizationList;